import { useDispatch, useSelector } from 'react-redux';
import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@client/model';
import { SessionType, getPolicyTypeFromTraditionalLTCQuote } from '@shared';
import { isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import {
  createPolicyFundingSourceFromQuote,
  parseBioPercentToInflationRate,
} from '@client/util/policyQuoteCalculations';
import type { Quote } from '@client/model/quotesModel';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  roiValues?: any;
  projectedCostCoverage?: number;
}

interface UseAddPolicyToLtcPlanningProps {
  clientPartner?: Client | null;
}

/**
 * Hook to handle adding policies (both life insurance and traditional LTC) as funding sources
 */
export function useAddPolicyToLtcPlanning({
  clientPartner = null,
}: UseAddPolicyToLtcPlanningProps = {}) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const addPolicy = (quote: QuoteWithROI | TraditionalLTCQuoteWithROI) => {
    if (!client?.clientId) {
      console.warn('Cannot add policy: client not found');
      return;
    }

    // Handle Traditional LTC quotes
    if (isTraditionalLTCQuote(quote)) {
      const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);

      const getPremiumCostPerMonth = (quote: TraditionalLTCQuote): number => {
        switch (quote.premiumFrequency) {
          case 'Monthly':
            return quote.primaryModalPremium;
          case 'Annually':
            return quote.primaryModalPremium / 12;
          default:
            return quote.primaryModalPremium / 12;
        }
      };

      const traditionalLTCPolicy = {
        carrierName: quote.carrier,
        hasPolicyFunding: true,
        isSimpleInflationProtection: false,
        addedByAdvisorId: isAdvisorSession ? advisor?.advisorId : undefined,
        isIndemnityPolicyPayment: false,
        policyBenefitIsUnlimited: false,
        policyMaximumBenefitAmount: quote.benefitPool,
        policyPremiumMonthlyCost: getPremiumCostPerMonth(quote),
        policyPremiumStartYear: new Date().getFullYear(),
        policyInflationProtection: inflationRate,
        policyLumpSumPayment: null,
        dailyHomeBenefitAmount: quote.dailyBenefitAmount,
        dailyHomeInflationProtectionPercent: null,
        dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
        dailyIndependentLivingInflationProtectionPercent: null,
        dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
        dailyAssistedLivingInflationProtectionPercent: null,
        dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
        dailyNursingHomeInflationProtectionPercent: null,
        homeCareWaitingPeriodDays: quote.eliminationPeriod,
        facilityCareWaitingPeriodDays: quote.eliminationPeriod,
        policyLimitedPayYears: null,
        policyBenefitPeriodMonths: null,
        policyMonthlyWithdrawalRate: null,
        policyPremiumIncreaseRate: null,
        policyPremiumIncreaseYears: null,
        policyInflationProtectionOnFaceAmount: null,
        policyInflationProtectionOnCOBAmount: null,
        policyInflationProtectionYears: null,
        policyBenefitUtilizedToDate: 0,
        policyContinuationBenefitAmount: null,
        policyMinimumGuaranteedBenefitAmount: null,
        customPolicyId: quote.traditionalLTCQuoteId,
        addedViaShopInsurance: true,
        insuranceQuoteId: quote.traditionalLTCQuoteId,
        sourceQuote: quote,
        policyType: getPolicyTypeFromTraditionalLTCQuote(quote),
        policyAddedAt: Date.now(),
      };

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: traditionalLTCPolicy,
          fundingSourceId: null,
        }),
      );
    } else {
      // Handle regular life insurance quotes
      const lifeQuote = quote as QuoteWithROI;

      // Create funding source using the quote (with ROI values already calculated)
      const finalPolicy = createPolicyFundingSourceFromQuote(
        lifeQuote,
        client,
        isAdvisorSession,
        advisor?.advisorId,
        lifeQuote, // Pass the quote as updatedQuote to preserve ROI values
        null, // No illustration PDF URL
      );

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: finalPolicy,
          fundingSourceId: null,
        }),
      );

      // For joint policies, also add to partner
      if (lifeQuote.policyType === 'Joint Life' && clientPartner) {
        const partnerFinalPolicy = createPolicyFundingSourceFromQuote(
          lifeQuote,
          clientPartner,
          isAdvisorSession,
          advisor?.advisorId,
          lifeQuote,
          null,
        );

        dispatch(
          putMultipleFundingSourceForClientByClientIdRequest({
            clientId: clientPartner.clientId,
            fundingSourceType: 'ltcPolicy',
            fundingSourceData: partnerFinalPolicy,
            fundingSourceId: null,
          }),
        );
      }
    }
  };

  return { addPolicy };
}
